import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { DTypeRepository, DTypeStatus, NextLocator, Pagination, SelectOption } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { format } from 'date-fns';
import * as lodash from 'lodash-es';
import { catchError, combineLatest, debounceTime, firstValueFrom, merge, Subject, takeUntil, throwError } from 'rxjs';
import { COMPLEX_URI } from '../../complex/complex.service';
import { DownloadGoalModelBody, UploadGoalExcelBody } from '../dtype.types';
import { UserNodePrivileges } from '../../../modules/admin/projects/projects.types';
import { ServiceTypology } from '../../../modules/admin/services/services.types';
import { NextTaskType, TASK_TYPE_URI } from '../../task-type/task-type.types';
import { DTypeService } from '../dtype.service';
import { LocalizePipe } from '../../pipes/localize.pipe';
import { TaskTypeService } from '../../task-type/task-type.service';
import { DialogsService } from '../../../dialogs/dialogs.service';
import { HttpService } from '../../http/http.service';
import { ProjectsService } from '../../../modules/admin/projects/projects.service';
import { ServicesService } from '../../../modules/admin/services/services.service';
import { NextMethodsEnum } from '../../enums/next-methods.enum';
import { Utils } from '../../utils/utils';
import { MASTER_DATA_URI } from '../../master-data/master-data.service';
import { FORM_URI } from '../../form/form.service';
export const UPLOAD_GOAL_EXCEL_URI = 'excelupload://com.cloudcodice.thirdparty.grupoconstant.poixssf.DTypeExcelUpload';
@Component({
    selector: 'dtype-facade-list',
    templateUrl: './dtype-facade-list.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DTypeFacadeListComponent implements OnInit, OnDestroy, AfterViewInit {

    @ViewChild(MatSort) _sort: MatSort;
    @ViewChild(MatPaginator) _paginator: MatPaginator;

    repoList: (DTypeRepository | NextTaskType)[] = [];
    filteredRepoList: (DTypeRepository | NextTaskType)[] = [];
    viewList: (DTypeRepository | NextTaskType)[] = [];
    locator: string;
    captionLocator: string;
    facade: string;
    serviceTypology: ServiceTypology | undefined;
    showGoalsUpload = false;
    params: Params;
    role: UserNodePrivileges;

    searchControl = new FormControl('');
    readonly debounceTime = 400;

    pagination: Pagination;
    loading = true;
    includeHidden = false;

    typologies: SelectOption[];

    private initialized = new Subject<void>();
    private unsubscribeAll = new Subject<void>();
    private readonly masterTypologyClass = 'com.cloudcodice.feature.master.MasterTypology';
    private readonly formTypologyClass = 'com.cloudcodice.feature.dtype.facade.form.FormTypology';
    private readonly tasktypeTypologyClass = 'com.cloudcodice.thirdparty.grupoconstant.nexttasktype.NextTaskTypeTypology';

    constructor(
        private changeDetector: ChangeDetectorRef,
        private dtypeService: DTypeService,
        private localize: LocalizePipe,
        private route: ActivatedRoute,
        private router: Router,
        private taskTypeService: TaskTypeService,
        private transloco: TranslocoService,
        private httpService: HttpService,
        private dialogsService: DialogsService,
        private projectsService: ProjectsService,
        private _snackbar: MatSnackBar,
        private servicesService: ServicesService
    ) { }

    ngOnInit() {
        combineLatest([
            this.route.queryParams,
            this.route.url
        ]).pipe(takeUntil(this.unsubscribeAll))
            .subscribe(async ([params, url]) => {
                const [locator, stringLocator] = this.projectsService.buildLocatorFromParams(params);
                this.locator = stringLocator;
                this.facade = url[0].path;
                this.params = params;

                this.role = await this.projectsService.getRoleByLocators(params);
                if (this.facade === 'masterSystem' && !this.role?.systemConfig) {
                    this.router.navigateByUrl('/403');
                }

                if (this.facade === 'master') {
                    this.httpService.post<{ option: SelectOption[] }>(NextMethodsEnum.retrieveEnumerationType, { enumerationClass: this.masterTypologyClass })
                    .subscribe((reply) => {
                        this.typologies = reply.option.filter(typology => typology.code !== 'SYSTEM_CONFIG');
                    });
                } if (this.facade === 'form') {
                    this.httpService.post<{ option: SelectOption[] }>(NextMethodsEnum.retrieveEnumerationType, { enumerationClass: this.formTypologyClass })
                    .subscribe((reply) => this.typologies = reply.option);
                } else if (this.facade === 'tasktype') {
                    this.httpService.post<{ option: SelectOption[] }>(NextMethodsEnum.retrieveEnumerationType, { enumerationClass: this.tasktypeTypologyClass })
                    .subscribe(reply => this.typologies = reply.option);
                    if (locator.serviceId) {
                        const service = await firstValueFrom(this.servicesService.service$);
                        this.serviceTypology = service.typology;
                        this.showGoalsUpload = service.showGoalsUpload;
                    }
                } else if (this.facade === 'masterSystem') {
                    this.httpService.post<{ option: SelectOption[] }>(NextMethodsEnum.retrieveEnumerationType, { enumerationClass: this.masterTypologyClass })
                    .subscribe((reply) => {
                        this.typologies = reply.option.filter(typology => typology.code === 'SYSTEM_CONFIG');
                    });
                }
                if (this.facade === 'tasktype') {
                    const [collection, partaker] = await firstValueFrom(this.taskTypeService.retrieveTaskTypeListCollectionDN(locator));

                    for (const uris of Object.values(collection)) {
                        for (const tasktype of Object.values(uris)) {
                            tasktype.sameLocator = this.projectsService.isTheSameLocator(stringLocator, tasktype.belongsTo);
                            tasktype.belongsToCaption = this.projectsService.getBelongsToCaption(tasktype.belongsTo, partaker);
                            this.repoList.push(tasktype);
                        }
                    }
                    this.captionLocator = this.projectsService.getBelongsToCaption(this.locator, partaker);
                } else if (this.facade === 'masterSystem' && this.role?.systemConfig) {
                    const [collection, partaker] = await firstValueFrom(this.dtypeService.retrieveNextMasterSystemCollection({
                        locator,
                        typology: 'SYSTEM_CONFIG'
                    }));

                    const facadeUri = this.getUriFromFacade('master');
                    for (const loc of Object.keys(collection[facadeUri])) {
                        for (const repo of Object.values(collection[facadeUri][loc])) {
                            repo.sameLocator = this.projectsService.isTheSameLocator(stringLocator, repo.belongsTo);
                            repo.belongsToCaption = this.projectsService.getBelongsToCaption(repo.belongsTo, partaker);
                            this.repoList.push(repo);
                        }
                    }
                    this.captionLocator = this.projectsService.getBelongsToCaption(this.locator, partaker);
                } else {
                    const [collection, partaker] = await firstValueFrom(this.dtypeService.retrieveNextDtypeCollection({
                        locator,
                        facade: this.facade
                    }));

                    const facadeUri = this.getUriFromFacade(this.facade);
                    for (const loc of Object.keys(collection[facadeUri])) {
                        for (const repo of Object.values(collection[facadeUri][loc])) {
                            if (this.facade === 'complex' && repo.status === DTypeStatus.STATIC) {
                                continue;
                            }
                            repo.sameLocator = this.projectsService.isTheSameLocator(stringLocator, repo.belongsTo);
                            repo.belongsToCaption = this.projectsService.getBelongsToCaption(repo.belongsTo, partaker);
                            this.repoList.push(repo);
                        }
                    }
                    this.captionLocator = this.projectsService.getBelongsToCaption(this.locator, partaker);
                }

                this.filteredRepoList = lodash.cloneDeep(this.repoList).filter((r) => {
                    if (Utils.interfaceOf<NextTaskType>(r, 'taskTypeId')) {
                        return true;
                    } else {
                        return (this.includeHidden || !r.hidden);
                    }
                });
                this.pagination = {
                    length: this.filteredRepoList.length,
                    page: 0,
                    startIndex: 0,
                    endIndex: 10,
                    size: 10,
                    lastPage: Math.max(Math.ceil(this.filteredRepoList.length / 10), 1)
                };

                this.initialized.complete();
                this.loading = false;
            });

        this.searchControl.valueChanges
            .pipe(
                debounceTime(this.debounceTime),
                takeUntil(this.unsubscribeAll)
            )
            .subscribe(() => this.search());
    }

    ngOnDestroy(): void {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    ngAfterViewInit(): void {
        this.initialized.subscribe({
            complete: () => {
                this.changeDetector.detectChanges();
                if (this._paginator && this._sort) {
                    this._sort.sort({
                        id: 'belongsTo',
                        start: 'asc',
                        disableClear: true
                    });
                    this.paginateAndSort();

                    merge(
                        this._paginator.page,
                        this._sort.sortChange
                    )
                        .pipe(takeUntil(this.unsubscribeAll))
                        .subscribe(() => {
                            this.paginateAndSort();
                        });
                }
            }
        });
    }

    navigateToBelongsTo(repo: DTypeRepository | NextTaskType) {
        if (repo.sameLocator) {
            return;
        }
        const split = repo.belongsTo.split(',');
        const firstLocatorSplit = split[0].split('=');
        let secondLocatorSplit = [];
        if (split.length > 1) {
            secondLocatorSplit = split[1].split('='); // Only used for customers, it is the brand code
        }
        switch (firstLocatorSplit[0]) {
            case 'OU':
                this.router.navigate(['/ou']);
                break;
            case 'Brand':
                this.router.navigate(['/projects/edit-brand'], { queryParams: { marca_code: this.params.marca_code } });
                break;
            case 'Customer':
                this.router.navigate(['/projects/edit-customer'], {
                    queryParams: {
                        marca_code: this.params.marca_code,
                        cliente_code: this.params.cliente_code
                    }
                });
                break;
            case 'Project':
                this.router.navigate(['/projects/edit-project'], {
                    queryParams: {
                        marca_code: this.params.marca_code,
                        cliente_code: this.params.cliente_code,
                        projectId: this.params.projectId
                    }
                });
                break;
        }
    }

    getAddCaption() {
        let facadeTranslationKey = '';
        switch (this.facade) {
            case 'complex':
                facadeTranslationKey = 'complex';
                break;
            case 'master':
                facadeTranslationKey = 'masterData';
                break;
            case 'form':
                facadeTranslationKey = 'form';
                break;
            case 'tasktype':
                facadeTranslationKey = 'taskTypes';
                break;
        }

        const split = this.locator.split(',');
        const firstLocatorSplit = split[0].split('=');
        let secondLocatorSplit = [];
        if (split.length > 1) {
            secondLocatorSplit = split[1].split('='); // Only used for customers, it is the brand code
        }
        switch (firstLocatorSplit[0]) {
            case 'OU':
                return this.transloco.translate('addDTypeToOU', { dtype: this.transloco.translate(facadeTranslationKey).toLowerCase() });
            case 'Brand':
                return this.transloco.translate('addDTypeToBrand', { dtype: this.transloco.translate(facadeTranslationKey).toLowerCase() });
            case 'Customer':
                return this.transloco.translate('addDTypeToCustomer', { dtype: this.transloco.translate(facadeTranslationKey).toLowerCase() });
            case 'Project':
                return this.transloco.translate('addDTypeToProject', { dtype: this.transloco.translate(facadeTranslationKey).toLowerCase() });
            case 'Service':
                return this.transloco.translate('addDTypeToService', { dtype: this.transloco.translate(facadeTranslationKey).toLowerCase() });
            default:
                return '';
        }
    }

    getTypology(typology: string) {
        return this.typologies?.find(t => t.code === typology)?.value;
    }

    hasPermissionToDesignRepository() {
        if (this.facade === 'masterSystem') {
            return false;
        }
        return this.projectsService.hasPermissionToDesignRepository(this.role, this.facade);
    }

    deleteRepository(repo: DTypeRepository | NextTaskType) {
        // const dialogRef = this.fuseConfirmationService.open({
        //     title: this.transloco.translate('warning'),
        //     message: this.transloco.translate('deleteDTypeConfirm'),
        //     actions: {
        //         confirm: {
        //             label: this.transloco.translate('confirm')
        //         },
        //         cancel: {
        //             label: this.transloco.translate('cancel')
        //         }
        //     }
        // });

        // dialogRef.afterClosed().subscribe((result) => {
        //     if (result === 'confirmed') {
        //         this.httpService.post<any>(NextMethodsEnum.deleteDType, { uri: _uri })
        //         .pipe(
        //             catchError((error) => {
        //                 console.error(error);
        //                 this.dialogsService.openErrorDialog({
        //                     message: error?.error?.echo ? this.localize.transform(error?.error?.echo) : undefined,
        //                 });
        //                 return throwError(() => error);
        //             })
        //         ).subscribe((reply) => {
        //             if (reply.status === 200) {
        //                 this.viewList = this.viewList.filter(item => item.uri !== _uri);
        //                 this.repoList = this.repoList.filter(item => item.uri !== _uri);
        //             }
        //             this._snackbar.open(this.transloco.translate('deletedDType', { name: _uri}), this.transloco.translate('accept'), { duration: 5000 });
        //         });
        //     }
        // });
        const name = Utils.interfaceOf<NextTaskType>(repo, 'taskTypeId') ? repo.name : this.localize.transform(repo.caption);
        this.dtypeService.deleteNextDType(repo.uri).subscribe({
            next: (reply) => {
                if (reply?.status === 200) {
                    this.viewList = this.viewList.filter(item => item.uri !== repo.uri);
                    this.filteredRepoList = this.filteredRepoList.filter(item => item.uri !== repo.uri);
                    this.repoList = this.repoList.filter(item => item.uri !== repo.uri);
                    this._snackbar.open(this.transloco.translate('deletedDType', { name }), this.transloco.translate('accept'), { duration: 5000 });
                } else {
                    this.dialogsService.openErrorDialog();
                }
            }, error: (err) => {
                console.error(err);
                this.dialogsService.openErrorDialog({
                    message: err?.error?.echo ? this.localize.transform(err?.error?.echo) : undefined,
                });
            }
        });
    }

    downloadGoalModel() {
        const locator = this.projectsService.buildLocatorFromParams(this.params)[0];
        if (!locator?.serviceId) {
            this.dialogsService.openErrorDialog({message: this.transloco.translate('cantDownloadGoals')});
        }
        const body: DownloadGoalModelBody = {
            serviceId: locator.serviceId,
        };

        const date = (format(new Date(), 'yyyyMMddHHmmss'));
        const filenameFront = 'goalModel' + body.serviceId + '_d_' + date;
        this.dtypeService.downloadGoalModel(body).pipe(
            catchError((error) => {
                console.error(error);
                let _message = null;
                if (error?.status === 404) {
                    _message = 'No se ha encontrado el servicio.';
                }
                if (error?.status === 403) {
                    _message = 'No hay tipos de tarea válidos asociados al servicio.';
                }
                this.dialogsService.openErrorDialog({
                    message: _message ? _message : undefined,
                });
                return throwError(() => error);
            })).subscribe((data) => {
                const filename = String(data?.headers?.get('content-disposition')).replace('attachment; filename="', '').replace('"', '');
                const element = document.createElement('a');
                element.href = window.URL.createObjectURL(data.body);
                element.download = filename ?? filenameFront;
                element.click();
            });
    }

    hiddenChanged() {
        // Reset back to the first page
        this._paginator.pageIndex = 0;
        this.search();
    }

    async openUploadDialog() {
        const dtype = await this.dtypeService.getDatatype({ retrieveDTypeBody: { uri: UPLOAD_GOAL_EXCEL_URI, bindableOptions: false }, includeEmbedded: true });
        this.dialogsService.openUploadExcelDialog(dtype, '605px').afterClosed().subscribe((query) => {
            if (query) {
                const locator: NextLocator = this.projectsService.buildLocatorFromParams(this.params)[0];
                const body: UploadGoalExcelBody = {
                    locator,
                    excelType: 'GOALS',
                    async: true,
                    file: query?.file
                };
                this.dtypeService.uploadExcel(body).pipe(
                    catchError((error) => {
                        console.error(error);
                        this.dialogsService.openErrorDialog({
                            message: error?.error?.echo ? this.localize.transform(error?.error?.echo) : undefined,
                        });
                        return throwError(() => error);
                    })
                ).subscribe(async (data) => {
                    this._snackbar.open(this.transloco.translate('excelDataProcessAsync'), this.transloco.translate('accept'), { duration: 5000 });
                });
            }
        });
    }

    private paginateAndSort() {
        const repoList = [...this.filteredRepoList];
        this.sort(repoList);

        const begin = this._paginator.pageIndex * this._paginator.pageSize;
        const end = Math.min((this._paginator.pageSize * (this._paginator.pageIndex + 1)), repoList.length);
        this.viewList = repoList.slice(begin, end);
    }

    private sort(repoList: (DTypeRepository | NextTaskType)[]) {
        const sortActive = this._sort.active;
        const sortDirection = this._sort.direction;
        repoList.sort((a: NextTaskType | DTypeRepository, b: NextTaskType | DTypeRepository) => {
            let compare = 0;
            if (sortActive === 'name') {
                if (this.facade === 'tasktype') {
                    compare = (a as NextTaskType).name.localeCompare((b as NextTaskType).name);
                } else {
                    compare = this.localize.transform((a as DTypeRepository).caption).localeCompare(this.localize.transform((b as DTypeRepository).caption));
                }
            } else if (sortActive === 'belongsTo') {
                compare = this.compareBelongsTo(a, b);
            } else {
                compare = this.compareStatus(a as DTypeRepository, b as DTypeRepository);
            }
            return sortDirection === 'asc' ? compare : -compare;
        });
    }

    private search() {
        if (this.searchControl.value === '') {
            this.clearSearch();
            return;
        }
        if (this._paginator) {
            this._paginator.pageIndex = 0;
        }
        const list = lodash.cloneDeep(this.repoList);
        this.filteredRepoList = list.filter((r) => {
            if (Utils.interfaceOf<NextTaskType>(r, 'taskTypeId')) {
                return Utils.removeAccents(r.name.toLowerCase()).includes(Utils.removeAccents(this.searchControl.value.toLowerCase()));
            } else {
                return Utils.removeAccents(this.localize.transform(r.caption).toLowerCase()).includes(Utils.removeAccents(this.searchControl.value.toLowerCase())) &&
                (this.includeHidden || !r.hidden);
            }
        });
        this.pagination = {
            length: this.filteredRepoList.length,
            page: 0,
            startIndex: 0,
            endIndex: this._paginator.pageSize,
            size: this._paginator.pageSize,
            lastPage: Math.max(Math.ceil(this.filteredRepoList.length / this._paginator.pageSize), 1)
        };
        this.paginateAndSort();
    }

    private clearSearch(): void {
        this.searchControl.setValue('', { emitEvent: false });
        if (this._paginator) {
            this._paginator.pageIndex = 0;
        }
        this.filteredRepoList = lodash.cloneDeep(this.repoList).filter((r) => {
            if (Utils.interfaceOf<NextTaskType>(r, 'taskTypeId')) {
                return true;
            } else {
                return (this.includeHidden || !r.hidden);
            }
        });
        this.pagination = {
            length: this.filteredRepoList.length,
            page: 0,
            startIndex: 0,
            endIndex: this._paginator.pageSize,
            size: this._paginator.pageSize,
            lastPage: Math.max(Math.ceil(this.filteredRepoList.length / this._paginator.pageSize), 1)
        };
        this.paginateAndSort();
    }

    private compareBelongsTo(a: DTypeRepository | NextTaskType, b: DTypeRepository | NextTaskType) {
        const aBelongsTo = a.belongsTo.split(',')[0].split('=')[0];
        const bBelongsTo = b.belongsTo.split(',')[0].split('=')[0];
        if (aBelongsTo === bBelongsTo) {
            if (this.facade === 'tasktype') {
                return (a as NextTaskType).name.localeCompare((b as NextTaskType).name);
            } else {
                return this.localize.transform((a as DTypeRepository).caption).localeCompare(this.localize.transform((b as DTypeRepository).caption));
            }
        } else {
            switch (aBelongsTo) {
                case 'Service':
                    return -1;
                case 'Project':
                    return bBelongsTo === 'Service' ? 1 : -1;
                case 'Customer':
                    return bBelongsTo === 'Service' || bBelongsTo === 'Project' ? 1 : -1;
                case 'Brand':
                    return bBelongsTo === 'OU' ? -1 : 1;
                case 'OU':
                    return 1;
            }
        }
    }

    private compareStatus(a: DTypeRepository, b: DTypeRepository) {
        if (a.status === b.status) {
            return this.localize.transform(a.caption).localeCompare(this.localize.transform(b.caption));
        }
        switch (a.status) {
            case DTypeStatus.LIVE:
                return -1;
            case DTypeStatus.STATIC:
                return b.status === DTypeStatus.LIVE ? 1 : -1;
            case DTypeStatus.DESIGN:
                return 1;
        }
    }

    private getUriFromFacade(facade: string) {
        switch (facade) {
            case 'complex':
                return COMPLEX_URI;
            case 'master':
                return MASTER_DATA_URI;
            case 'form':
                return FORM_URI;
            case 'tasktype':
                return TASK_TYPE_URI;
        }
    }
}
